import { defineComponent, PropType } from "vue";
import SettingsMarketTestSkeleton from "./skeleton/component.vue";
import { ApplicationType, RoutesName } from "@/shared/constants/enums";
import {
  knowledgeQuestions,
  knowledgeAssessmentRules,
  knowledgeRisks,
} from "./questions";
import {
  KnowledgeTestResult,
  KnowledgeSubmissionState,
  KnowledgeTestDraft,
  knowledgeClient,
  knowledgeFilledDate,
  initialKnowledgeSelections,
} from "./model";

import { SettingsMarketTestState } from "./state";
import { SettingsMarketTestQuestion } from "./question";
import { SettingsMarketTestRisks } from "./risks";
import { SettingsMarketTestFooter } from "./footer";

type KnowledgeStateAction = "verification" | "retry" | "reload";

interface KnowledgeStateView {
  props: {
    icon?: string;
    title: string;
    text?: string;
    score?: number | null;
    passed?: boolean;
    buttonLabel?: string;
    buttonDisabled?: boolean;
  };
  role: "status" | "alert";
  live?: "polite";
  action?: KnowledgeStateAction;
}

export default defineComponent({
  name: "settings-market-test",
  components: {
    SettingsMarketTestSkeleton,
    SettingsMarketTestState,
    SettingsMarketTestQuestion,
    SettingsMarketTestRisks,
    SettingsMarketTestFooter,
  },
  props: {
    result: {
      type: Object as PropType<KnowledgeTestResult | null>,
      default: null,
    },
    submissionState: {
      type: String as PropType<KnowledgeSubmissionState>,
      default: "idle",
    },
    submissionError: { type: String, default: "" },
  },
  emits: {
    submit: (draft: KnowledgeTestDraft) => Array.isArray(draft.selections),
  },
  data() {
    return {
      questions: knowledgeQuestions,
      assessmentRules: knowledgeAssessmentRules,
      risks: knowledgeRisks,
      selections: initialKnowledgeSelections(knowledgeQuestions.length),
      risksAcknowledged: false,
      filledDate: knowledgeFilledDate(),
      client: knowledgeClient(null),
      identityLoading: false,
      identityError: "",
      submissionNotice: "",
      retrying: false,
      requestVersion: 0,
      userRepo: this.$projectServices.userRepo,
      kycRepository: this.$projectServices.kycRepository,
      kybRepository: this.$projectServices.kybRepository,
    };
  },
  computed: {
    isVerified(): boolean {
      return this.userRepo.isVerified;
    },
    identityKey(): string {
      return `${this.userRepo.userInfo.id}:${this.userRepo.userInfo.user_info.application_type}:${this.isVerified}`;
    },
    answeredCount(): number {
      return this.questions.filter(
        (question, index) =>
          Number.isInteger(this.selections[index]) &&
          !!question.options[this.selections[index]]
      ).length;
    },
    canSubmit(): boolean {
      return (
        this.isVerified &&
        this.client.complete &&
        !this.identityLoading &&
        !this.identityError &&
        this.risksAcknowledged &&
        this.answeredCount === this.questions.length &&
        this.submissionState !== "submitting" &&
        !this.visibleResult
      );
    },
    visibleResult(): KnowledgeTestResult | null {
      return this.result?.status === "passed" || !this.retrying
        ? this.result
        : null;
    },
    stateView(): KnowledgeStateView | null {
      if (!this.isVerified)
        return {
          props: {
            icon: "!",
            title: "Сначала пройдите верификацию",
            text: "Тест станет доступен после успешного подтверждения KYC/KYB. Отправки анкеты на проверку недостаточно.",
            buttonLabel: "Перейти к верификации",
          },
          role: "status",
          action: "verification",
        };
      if (this.visibleResult) {
        const passed = this.visibleResult.status === "passed";
        return {
          props: {
            icon: passed ? "✓" : "×",
            title: passed ? "Тест пройден" : "Тест не пройден",
            text: passed
              ? "Уровень знаний достаточный."
              : "При результате ниже установленного порога клиенту не предоставляется доступ к операциям до повторного тестирования после ознакомления с информацией о виртуальных активах и рисках.",
            score: this.visibleResult.score,
            passed,
            buttonLabel: passed ? "" : "Пройти тест заново",
            buttonDisabled: this.submissionState === "submitting",
          },
          role: "status",
          action: "retry",
        };
      }
      if (this.identityLoading)
        return {
          props: { title: "Загрузка данных клиента…" },
          role: "status",
          live: "polite",
        };
      if (this.identityError)
        return {
          props: {
            title: "Не удалось загрузить данные",
            text: this.identityError,
            buttonLabel: "Повторить загрузку",
          },
          role: "alert",
          action: "reload",
        };
      return null;
    },
    progressMessage(): string {
      if (!this.client.complete)
        return this.$t("Отсутствуют обязательные данные клиента");
      if (!this.risksAcknowledged)
        return this.$t("Подтвердите ознакомление с рисками");
      return this.$t("Всё готово");
    },
  },
  watch: {
    identityKey: {
      immediate: true,
      handler() {
        this.resetAttempt();
        void this.loadIdentity();
      },
    },
    result() {
      this.retrying = false;
      this.submissionNotice = "";
    },
  },
  beforeUnmount() {
    this.requestVersion++;
  },
  methods: {
    async loadIdentity(): Promise<void> {
      const version = ++this.requestVersion;
      this.client = knowledgeClient(
        this.userRepo.userInfo.user_info.application_type
      );
      this.identityError = "";
      this.identityLoading = false;
      if (!this.isVerified) return;
      this.identityLoading = true;
      const { id, user_info } = this.userRepo.userInfo;
      try {
        if (user_info.application_type === ApplicationType.kyc) {
          const personal = await this.kycRepository.personalInfo(id);
          if (version === this.requestVersion && this.isVerified)
            this.client = knowledgeClient(ApplicationType.kyc, personal);
        } else if (user_info.application_type === ApplicationType.kyb) {
          const [company, participants] = await Promise.all([
            this.kybRepository.basicInfo(id),
            this.kybRepository.participants(id),
          ]);
          if (version === this.requestVersion && this.isVerified)
            this.client = knowledgeClient(
              ApplicationType.kyb,
              undefined,
              company,
              participants
            );
        }
      } catch {
        if (version === this.requestVersion)
          this.identityError =
            "Не удалось загрузить данные подтверждённой верификации.";
      } finally {
        if (version === this.requestVersion) this.identityLoading = false;
      }
    },
    resetAttempt(): void {
      this.selections = initialKnowledgeSelections(this.questions.length);
      this.risksAcknowledged = false;
      this.filledDate = knowledgeFilledDate();
      this.submissionNotice = "";
    },
    retry(): void {
      if (
        !this.isVerified ||
        this.result?.status !== "failed" ||
        this.submissionState === "submitting"
      )
        return;
      this.retrying = true;
      this.resetAttempt();
    },
    submit(): void {
      if (!this.canSubmit) return;
      this.submissionNotice =
        "Ответы не отправлены. Отправка теста временно недоступна.";
      // No POST, local score, persistence or admission. A parent can consume this
      // draft after the backend option IDs and response contract are agreed.
      this.$emit("submit", {
        selections: [...this.selections],
        risksAcknowledged: this.risksAcknowledged,
        filledDate: this.filledDate,
      });
    },
    handleStateAction(): void {
      const action = this.stateView?.action;
      if (action === "verification") this.openVerification();
      else if (action === "retry") this.retry();
      else if (action === "reload") void this.loadIdentity();
    },
    openVerification(): void {
      this.$router.push({ name: RoutesName.verification });
    },
  },
});
